import { motion } from 'framer-motion';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/SEOHead';

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://dsfcartech.be';

  const schema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "Over DSF Cartech",
    "description": "DSF Cartech is gespecialiseerd in professionele auto-upgrades in Antwerpen. Van CarPlay tot sfeerverlichting - wij transformeren uw auto.",
    "url": `${siteUrl}/over-ons`,
  };

  return (
    <>
      <SEOHead
        title="Over Ons - DSF Cartech Auto Elektronica Antwerpen"
        description="DSF Cartech is uw specialist in auto elektronica upgrades in Antwerpen. Professionele installatie van CarPlay, Android Auto, sfeerverlichting, sterrenhemel en meer. Ervaren team met passie voor automotive technologie."
        keywords="over ons, DSF Cartech, auto elektronica Antwerpen, CarPlay specialist, Android Auto installatie, auto upgrades Antwerpen, automotive technologie"
      />
      
      <div className="bg-black text-white min-h-screen">
        <Navigation />
        
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(6, 182, 212, 0.3) 1px, transparent 0)',
              backgroundSize: '50px 50px'
            }}></div>
          </div>

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black"></div>

          {/* Content */}
          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center py-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Over <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">DSF Cartech</span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
                Uw specialist in premium auto elektronica upgrades in Antwerpen
              </p>
            </motion.div>
          </div>

          {/* Bottom Gradient */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent"></div>
        </section>

        {/* Our Story Section */}
        <section className="py-16 px-4 bg-black">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white text-center">
                Ons Verhaal
              </h2>
              
              <div className="space-y-6 text-gray-300 text-base md:text-lg leading-relaxed">
                <p>
                  <strong className="text-white">DSF Cartech</strong> is ontstaan uit pure passie voor auto's en technologie. 
                  Wat begon als een hobby om mijn eigen auto te upgraden, groeide uit tot een professionele dienstverlening 
                  waar ik elke dag met plezier mee bezig ben.
                </p>
                
                <p>
                  Vanuit onze werkplaats in <strong className="text-cyan-400">Wommelgem, Antwerpen</strong>, helpen we autobezitters 
                  hun wagen te transformeren met de nieuwste elektronica upgrades. Of het nu gaat om CarPlay installatie, 
                  een sfeervolle sterrenhemel, RGB verlichting of achteruitrijcamera's - wij maken uw auto compleet.
                </p>

                <p>
                  Wat ons onderscheidt? <strong className="text-white">Vakmanschap, aandacht voor detail en transparantie</strong>. 
                  Ik werk graag nauw samen met mijn klanten, begrijp hun wensen en lever maatwerk oplossingen die perfect 
                  bij hun auto en budget passen. Geen verborgen kosten, geen verrassingen - alleen eerlijk advies en kwalitatief werk.
                </p>

                <p>
                  Met jarenlange ervaring in auto-elektronica en een groeiende lijst van tevreden klanten 
                  (4.9 sterren op Google!), ben ik trots op wat we hebben opgebouwd. Elke installatie wordt 
                  met zorg uitgevoerd en komt met <strong className="text-cyan-400">volledige garantie</strong>.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Why DSF Cartech Section */}
        <section className="py-16 px-4 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Waarom DSF Cartech?
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: 'Garantie op Alle Installaties',
                  description: 'Volledige garantie op alle werkzaamheden en gebruikte materialen. Uw investering is beschermd.'
                },
                {
                  icon: 'ri-flashlight-line',
                  title: 'Installatie in 1 Dag',
                  description: 'Snelle en professionele service. De meeste upgrades worden dezelfde dag nog afgerond.'
                },
                {
                  icon: 'ri-star-line',
                  title: '4.9 Sterren Reviews',
                  description: 'Uitstekend beoordeeld door 41+ tevreden klanten op Google. Kwaliteit die spreekt.'
                },
                {
                  icon: 'ri-tools-line',
                  title: 'Gecertificeerde Expertise',
                  description: 'Jarenlange ervaring in auto-elektronica installaties met oog voor detail en perfectie.'
                },
                {
                  icon: 'ri-price-tag-3-line',
                  title: 'Transparante Prijzen',
                  description: 'Geen verborgen kosten of verrassingen. U krijgt vooraf een duidelijke offerte.'
                },
                {
                  icon: 'ri-customer-service-2-line',
                  title: 'Persoonlijk Maatwerk',
                  description: 'Luisteren naar uw wensen en adviseren wat het beste bij uw auto en budget past.'
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mb-4">
                    <i className={`${feature.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 px-4 bg-gradient-to-b from-gray-900 to-black">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-white">
              Onze Specialisaties
            </h2>
            <p className="text-center text-gray-300 mb-12 max-w-2xl mx-auto">
              Van moderne connectiviteit tot sfeervolle interieur upgrades - wij transformeren uw auto compleet
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: 'Smartphone Multimedia',
                  items: ['CarPlay Installatie', 'Android Auto', 'Draadloze Verbinding', 'Multimedia Systemen'],
                  icon: 'ri-smartphone-line',
                  gradient: 'from-cyan-500 to-blue-600'
                },
                {
                  title: 'Interieur Verlichting',
                  items: ['Sterrenhemel', 'RGB Sfeerverlichting', 'Ambient Lighting', 'LED Strips'],
                  icon: 'ri-lightbulb-flash-line',
                  gradient: 'from-purple-500 to-pink-600'
                },
                {
                  title: 'Camera Systemen',
                  items: ['Achteruitrijcamera', '360° View', 'Dashcam', 'Parking Sensors'],
                  icon: 'ri-camera-line',
                  gradient: 'from-orange-500 to-red-600'
                },
                {
                  title: 'Audio Upgrades',
                  items: ['Speaker Systemen', 'Subwoofers', 'Versterkers', 'Geluidsdemping'],
                  icon: 'ri-music-line',
                  gradient: 'from-green-500 to-teal-600'
                }
              ].map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 group"
                >
                  <div className={`w-14 h-14 bg-gradient-to-br ${service.gradient} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${service.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{service.title}</h3>
                  <ul className="space-y-2">
                    {service.items.map((item, i) => (
                      <li key={i} className="flex items-center text-gray-300 text-sm">
                        <i className="ri-check-line text-cyan-400 mr-2"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-12">
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/diensten')}
                className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold text-base rounded-xl transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
              >
                <span>Bekijk Alle Diensten</span>
                <i className="ri-arrow-right-line text-xl"></i>
              </button>
            </div>
          </div>
        </section>

        {/* Location Section */}
        <section className="py-16 px-4 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Bezoek Onze Werkplaats
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Map */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden h-96"
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2498.8!2d4.5247!3d51.1956!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c3f6e8e8e8e8e8%3A0x0!2sTermesselei%20191%2C%202160%20Wommelgem!5e0!3m2!1snl!2sbe!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="DSF Cartech Locatie"
                ></iframe>
              </motion.div>

              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 flex flex-col justify-center"
              >
                <h3 className="text-2xl font-bold mb-8 text-white">Contactgegevens</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-white text-xl"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-white mb-1">Adres</div>
                      <div className="text-gray-300">Ternesselei 191<br />2160 Wommelgem<br />Antwerpen, België</div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-white text-xl"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-white mb-1">Telefoon</div>
                      <a href="tel:0489170592" className="text-cyan-400 hover:text-cyan-300 transition-colors">
                        0489 17 05 92
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-white text-xl"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-white mb-1">Email</div>
                      <a href="mailto:Dsf.cartech@hotmail.com" className="text-cyan-400 hover:text-cyan-300 transition-colors">
                        Dsf.cartech@hotmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-white text-xl"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-white mb-1">Openingstijden</div>
                      <div className="text-gray-300">
                        Ma-Vr: 08:00 - 18:00<br />
                        Za: 09:00 - 16:00<br />
                        Zo: Gesloten
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/contact-afspraak-antwerpen')}
                  className="mt-8 w-full px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold text-base rounded-xl transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  <i className="ri-calendar-line text-xl"></i>
                  Maak een Afspraak
                </button>
              </motion.div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
